/**
 * The back wall of the stage (05 §5.2). The original's background was plain black; here it is
 * the rear of the cave: dark panels a little behind the blocks, each pushed in or out by a
 * seeded amount so the blocks' shadows and the fog have something to show on.
 */

import { Vector3 } from 'three';
import type { TileGrid } from '../../levels/grid';
import { Pcg32 } from '../../sim/rng';
import { MeshBuilder } from '../kit/MeshBuilder';
import type { Palette } from '../palette';
import { BLOCK_DEPTH } from './blocks';

/** How far behind the playfield plane the wall stands, in tiles. */
export const BACKDROP_Z = -(BLOCK_DEPTH / 2 + 1.2);
const PANEL = 2;

export function buildBackdrop(grid: TileGrid, p: Palette, seed: number): MeshBuilder {
  const mb = new MeshBuilder();
  const rng = new Pcg32(seed, 7);
  const margin = 4;
  const x0 = -grid.width / 2 - margin;
  const y0 = -grid.height / 2 - margin;
  const cols = Math.ceil((grid.width + margin * 2) / PANEL);
  const rows = Math.ceil((grid.height + margin * 2) / PANEL);
  const shades = [p.color('backdrop.0'), p.color('backdrop.1'), p.color('backdrop.1'), p.color('backdrop.2')];
  const v = (x: number, y: number, z: number): Vector3 => new Vector3(x, y, z);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = x0 + c * PANEL;
      const y = y0 + r * PANEL;
      const z = BACKDROP_Z + rng.range(-0.25, 0.1);
      const col = shades[rng.int(shades.length)] ?? shades[0];
      if (!col) continue;
      mb.quad(v(x, y, z), v(x + PANEL, y, z), v(x + PANEL, y + PANEL, z), v(x, y + PANEL, z), col);
      // Short sides down to the deepest panel so neighbouring steps never show a crack.
      const floor = BACKDROP_Z - 0.3;
      mb.quad(v(x, y + PANEL, z), v(x + PANEL, y + PANEL, z), v(x + PANEL, y + PANEL, floor), v(x, y + PANEL, floor), col);
      mb.quad(v(x, y, floor), v(x + PANEL, y, floor), v(x + PANEL, y, z), v(x, y, z), col);
      mb.quad(v(x, y, floor), v(x, y, z), v(x, y + PANEL, z), v(x, y + PANEL, floor), col);
      mb.quad(v(x + PANEL, y, z), v(x + PANEL, y, floor), v(x + PANEL, y + PANEL, floor), v(x + PANEL, y + PANEL, z), col);
    }
  }
  return mb;
}
